import React from 'react';

import { Upload, message, Typography, Modal, Space, Tooltip } from 'antd';
import {
  DeleteOutlined,
  FilePdfOutlined,
  UploadOutlined,
  EyeOutlined,
  LoadingOutlined,
  VideoCameraOutlined,
  FileTextOutlined,
  FilePptOutlined,
  FileZipOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { RcFile } from 'antd/lib/upload';

import { ClientContext } from '../../contexts';
import {
  MediaUploaderProps,
  MediaUploaderState,
  UploadedMediaType,
} from './types';
import { MediaApiResponseType } from 'src/types';

import styles from './MediaUploader.module.css';

const { Text } = Typography;
const { Dragger } = Upload;

// La taille maximale acceptée est 10 MB (10000000 Bytes)
const VALID_SIZE = 10000000;

const VALID_FORMAT_LIST = [
  'image/jpg',
  'image/jpeg',
  'image/png',
  'image/gif',
  'application/pdf',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.oasis.opendocument.text',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.oasis.opendocument.spreadsheet',
  'application/vnd.openofficeorg.extension',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/zip',
  'application/x-rar-compressed',
  'application/x-rar',
  'application/x-zip-compressed',
];

const VALID_VIDEO_LIST = [
  'video/x-flv',
  'video/mp4',
  'application/x-mpegURL',
  'video/MP2T',
  'video/3gpp',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-ms-wmv',
];

export class MediaUploader extends React.Component<
  MediaUploaderProps,
  MediaUploaderState
> {
  constructor(props: MediaUploaderProps) {
    super(props);

    this.state = {
      isUploading: false,
      mediaList: props.defaultValue || [],
      clientContext: undefined,
      previewPopoverStatus: {},
    };

    this.beforeUpload = this.beforeUpload.bind(this);
    this.handleUpload = this.handleUpload.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
    this.getMediaViewer = this.getMediaViewer.bind(this);
  }

  beforeUpload(file: RcFile) {
    const validFormatList = this.props.allExtensions
      ? VALID_FORMAT_LIST.concat(VALID_VIDEO_LIST)
      : VALID_FORMAT_LIST;

    const isFormatValid = validFormatList.includes(file.type);
    const isSizeValid = file.size < VALID_SIZE;
    if (!isFormatValid) {
      message.error(
        `Les formats acceptés sont les suivants : ${
          this.props.allExtensions
            ? '.jpg , .jpeg , .png , .gif , .pdf, .xls, docx, pptx, .mp4, .wmv, .avi'
            : '.jpg , .jpeg , .png , .gif , .pdf, .xls, docx, pptx'
        }`
      );
    }
    if (!isSizeValid) {
      message.error(
        'Veuillez importer une photo ou un document inférieur à 10 Mo'
      );
    }
    return isFormatValid && isSizeValid;
  }

  getBase64 = (img: any, callback: any) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => callback(reader.result));
    reader.readAsDataURL(img);
  };

  handleUpload(file: any) {
    const { onUpload, directory } = this.props;
    const { clientContext, previewPopoverStatus } = this.state;

    this.setState({ isUploading: true });

    if (clientContext) {
      const { upload, get } = clientContext;

      this.getBase64(file, (imageUrl: any) => {
        const media = {
          name: file.name,
          file: imageUrl,
        };

        upload(
          directory ? { directory: { id: directory }, ...media } : media,
          (mediaId: string) => {
            get(
              `/medias/${mediaId}`,
              (response: { data: MediaApiResponseType }) => {
                if (!response.data) return;

                if (this.props.withoutPersist) {
                  onUpload(response.data);
                } else {
                  this.setState(
                    {
                      mediaList: [
                        {
                          id: response.data.id,
                          url: response.data.url.original,
                          name: response.data.name,
                          type: response.data.mime,
                        },
                        ...this.state.mediaList,
                      ],
                      previewPopoverStatus: {
                        ...previewPopoverStatus,
                        [response.data.id]: false,
                      },
                      isUploading: false,
                    },
                    () => {
                      onUpload(this.state.mediaList);
                    }
                  );
                }
              }
            );
          },
          () =>
            message.success('Une erreur est survenue lors du de téléchargement')
        );
      });
    }
  }

  handleRemove(mediaId: string) {
    const { clientContext } = this.state;
    const { onUpload } = this.props;

    if (clientContext) {
      const { deletion } = clientContext;
      deletion(
        `/medias/${mediaId}`,
        {},
        () => {
          const filteredMedia = this.state.mediaList.filter(media => {
            if (media.uid) {
              return media.uid !== mediaId;
            } else {
              return media.id !== mediaId;
            }
          });

          this.setState(
            {
              mediaList: filteredMedia,
            },
            () => onUpload(filteredMedia)
          );
        },
        () => {
          message.error(
            'Une erreur est survenue lors de suppression de la photo ou du document'
          );
        }
      );
    }
  }

  getMediaIcon(media: UploadedMediaType) {
    if (VALID_VIDEO_LIST.includes(media.type))
      return (
        <>
          <VideoCameraOutlined className={styles.document} />
          <Tooltip placement="top" title={media.name}>
            <p className={styles.mediaName}>{media.name}</p>
          </Tooltip>
        </>
      );

    const extensionFileTab = media.name?.split('.');
    const extensionFile =
      extensionFileTab && extensionFileTab[extensionFileTab.length - 1];

    switch (extensionFile) {
      case 'doc':
      case 'docx':
        return (
          <>
            <FileTextOutlined className={styles.document} />
            <p className={styles.mediaName}>{media.name}</p>
          </>
        );
      case 'gif':
      case 'jpeg':
      case 'jpg':
      case 'png':
        return (
          <img src={media.url} alt={media.name} className={styles.image} />
        );
      case 'ppt':
      case 'pptx':
        return (
          <>
            <FilePptOutlined className={styles.document} />
            <Tooltip placement="top" title={media.name}>
              <p className={styles.mediaName}>{media.name}</p>
            </Tooltip>
          </>
        );
      case 'zip':
      case 'rar':
        return (
          <>
            <FileZipOutlined className={styles.document} />
            <Tooltip placement="top" title={media.name}>
              <p className={styles.mediaName}>{media.name}</p>
            </Tooltip>
          </>
        );
      case 'xls':
      case 'xlsx':
      case 'csv':
        return (
          <>
            <FileExcelOutlined className={styles.document} />
            <Tooltip placement="top" title={media.name}>
              <p className={styles.mediaName}>{media.name}</p>
            </Tooltip>
          </>
        );
      case 'pdf':
        return (
          <>
            <FilePdfOutlined className={styles.document} />
            <Tooltip placement="top" title={media.name}>
              <p className={styles.mediaName}>{media.name}</p>
            </Tooltip>
          </>
        );
      default:
        return (
          <img src={media.url} alt={media.name} className={styles.image} />
        );
    }
  }

  getMediaViewer(media: UploadedMediaType) {
    const { previewPopoverStatus } = this.state;

    return (
      <div
        key={media.url}
        className={this.props.className || styles.mediaContainer}
        data-testid="media-uploader-view"
      >
        {this.getMediaIcon(media)}
        <Space size="middle">
          <EyeOutlined
            onClick={() =>
              this.setState({
                previewPopoverStatus: {
                  ...previewPopoverStatus,
                  [media.id]: true,
                },
              })
            }
          />
          <Modal
            open={previewPopoverStatus[media.id]}
            title={media.name}
            footer={null}
            onCancel={() =>
              this.setState({
                previewPopoverStatus: {
                  ...previewPopoverStatus,
                  [media.id]: false,
                },
              })
            }
          >
            {VALID_VIDEO_LIST.includes(media.type) ? (
              <video controls width="450px">
                <source src={media.url} type={media.type} />
              </video>
            ) : (
              <img alt="example" style={{ width: '100%' }} src={media.url} />
            )}
          </Modal>
          <DeleteOutlined
            className={styles.removeIcon}
            onClick={() =>
              !this.props.disabled && this.handleRemove(media.uid || media.id)
            }
          />
        </Space>
      </div>
    );
  }

  renderDragger() {
    return (
      <Dragger
        action={'https://www.mocky.io/v2/5cc8019d300000980a055e76'}
        multiple={true}
        name="file"
        data={this.handleUpload}
      >
        <p className="ant-upload-drag-icon">
          <UploadOutlined />
        </p>
        <p className="ant-upload-text">
          Glisser et déposer un document de mon ordinateur
        </p>
      </Dragger>
    );
  }

  renderUploader() {
    const {
      placeholder,
      children,
      limit,
      defaultValue,
      pictureListType,
      disabled,
      isUploadButton,
    } = this.props;
    const { mediaList, isUploading } = this.state;

    return isUploadButton ? (
      <Upload
        data-testid="media-uploader-button"
        action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
        beforeUpload={this.beforeUpload}
        data={this.handleUpload}
        multiple={limit ? limit > 1 : true}
        disabled={disabled}
        showUploadList={false}
      >
        {children}
      </Upload>
    ) : (
      <div>
        {placeholder && pictureListType === 'picture-card' && (
          <Text strong={true}>{placeholder}</Text>
        )}
        <div
          className={!limit ? styles.uploadeContainer : undefined}
          style={{ marginTop: '15px' }}
        >
          {mediaList.map((media: UploadedMediaType) =>
            this.getMediaViewer(media)
          )}
          {(!limit || (limit && mediaList.length < limit)) && (
            <div>
              <Upload
                data-testid="media-uploader"
                action="https://www.mocky.io/v2/5cc8019d300000980a055e76"
                listType={pictureListType}
                fileList={this.props.canRemove ? [] : defaultValue}
                beforeUpload={this.beforeUpload}
                data={this.handleUpload}
                multiple={limit ? limit > 1 : true}
                disabled={disabled}
              >
                {isUploading ? <LoadingOutlined /> : children}
              </Upload>
            </div>
          )}
        </div>
      </div>
    );
  }

  render() {
    const { clientContext } = this.state;
    const { isDragger } = this.props;

    return (
      <ClientContext.Consumer>
        {context => {
          !clientContext && this.setState({ clientContext: context });
          return isDragger ? this.renderDragger() : this.renderUploader();
        }}
      </ClientContext.Consumer>
    );
  }
}
